/**
 * CAPTCHA discreto (Cloudflare Turnstile), modo `managed` + `interaction-only`.
 *
 * Nada aparece na tela enquanto a Cloudflare não exigir interação, e o script só
 * é baixado no primeiro envio de formulário — a home pública não paga esse custo.
 *
 * Por que não o modo `invisible`: ele não tem fallback. Quando o cliente parece
 * suspeito (VPN, navegador rígido, headless), o widget falha com `600010` em vez
 * de apresentar um desafio, e o usuário legítimo fica sem saída. Com `managed`,
 * o desafio aparece só nesse caso.
 *
 * A sitekey vem de `/api/public/config`. Sem sitekey (dev sem CAPTCHA), devolve
 * string vazia e o backend decide o que fazer.
 */

const SCRIPT_URL = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit'
// Generoso de propósito: se a Cloudflare pedir interação, quem resolve é um humano.
const EXECUTE_TIMEOUT_MS = 120_000

declare global {
  interface Window {
    turnstile?: {
      render: (el: HTMLElement, opts: Record<string, unknown>) => string
      execute: (id: string) => void
      remove: (id: string) => void
    }
    __turnstile_ready?: () => void
  }
}

export type CaptchaState = 'ready' | 'not_configured' | 'unavailable'

/**
 * `null` significava DUAS coisas opostas: "não há CAPTCHA configurado" (dev, e
 * aí seguir sem token é o certo) e "não consegui descobrir se há" (aí seguir
 * sem token é mandar o backend recusar e a culpa cair no usuário).
 */
export function resolve_captcha_state(input: { sitekey: string | null; fetch_failed: boolean }): CaptchaState {
  if (input.fetch_failed) return 'unavailable'
  return String(input.sitekey || '').trim() ? 'ready' : 'not_configured'
}

/** Texto único do aviso: é AGORA, e nada foi enviado. */
export function captcha_unavailable_message(): string {
  return 'A verificação anti-robô está indisponível no momento: nada foi enviado e nada foi alterado. Tente de novo em instantes.'
}

export class CaptchaUnavailableError extends Error {
  constructor(readonly cause_code: string) {
    super(captcha_unavailable_message())
    this.name = 'CaptchaUnavailableError'
  }
}

/**
 * Só o SUCESSO é memoizado.
 *
 * Antes a **promessa** era memoizada, inclusive quando resolvia `null` por
 * falha: uma oscilação de rede de um segundo deixava a sitekey nula pelo resto
 * da vida da aba, e todo envio seguinte ia sem token. Agora a falha é
 * descartada e a próxima tentativa busca de novo.
 */
let sitekey_cache: string | null = null
let script_promise: Promise<void> | null = null

async function resolve_sitekey(): Promise<{ sitekey: string | null; state: CaptchaState }> {
  if (sitekey_cache) return { sitekey: sitekey_cache, state: 'ready' }

  let fetch_failed = false
  let sitekey: string | null = null
  try {
    const res = await fetch('/api/public/config')
    if (!res.ok) {
      fetch_failed = true
    } else {
      const data = await res.json()
      sitekey = data?.turnstile_sitekey ? String(data.turnstile_sitekey) : null
    }
  } catch {
    fetch_failed = true
  }

  const state = resolve_captcha_state({ sitekey, fetch_failed })
  if (state === 'ready' && sitekey) sitekey_cache = sitekey
  return { sitekey, state }
}

function load_script(): Promise<void> {
  if (script_promise) return script_promise

  script_promise = new Promise<void>((resolve, reject) => {
    if (window.turnstile) return resolve()
    const script = document.createElement('script')
    script.src = SCRIPT_URL
    script.async = true
    script.defer = true
    script.onload = () => resolve()
    script.onerror = () => {
      script_promise = null
      reject(new Error('turnstile_script_failed'))
    }
    document.head.appendChild(script)
  })
  return script_promise
}

/**
 * Resolve com o token de uma única utilização. Lança em falha de rede/script —
 * o chamador deve mostrar erro em vez de enviar sem token, porque o backend
 * rejeita mesmo (fail closed).
 */
export async function get_turnstile_token(): Promise<string> {
  const { sitekey, state } = await resolve_sitekey()

  // "Não configurado" é o único caso em que seguir sem token é correto: é o dev
  // sem CAPTCHA, e o backend sabe disso.
  if (state === 'not_configured') return ''
  if (state === 'unavailable' || !sitekey) throw new CaptchaUnavailableError('config_fetch_failed')

  // Script bloqueado por adblock cai aqui e vira a MESMA mensagem: para quem
  // está do outro lado, "a Cloudflare não carregou" e "a extensão bloqueou" são
  // o mesmo fato — não dá para enviar agora.
  try {
    await load_script()
  } catch {
    throw new CaptchaUnavailableError('script_blocked')
  }
  const turnstile = window.turnstile
  if (!turnstile) throw new CaptchaUnavailableError('script_missing')

  // Container real (não `display:none`): se a Cloudflare pedir interação, o widget
  // precisa ser visível e clicável. Fica centralizado sobre a página.
  const holder = document.createElement('div')
  holder.setAttribute('data-turnstile-holder', '')
  holder.className = 'fixed inset-x-0 bottom-4 z-50 mx-auto flex w-fit justify-center'
  document.body.appendChild(holder)

  return new Promise<string>((resolve, reject) => {
    let widget_id: string | null = null
    const cleanup = () => {
      clearTimeout(timer)
      try {
        if (widget_id) turnstile.remove(widget_id)
      } catch {
        /* widget já removido */
      }
      holder.remove()
    }
    const timer = setTimeout(() => {
      cleanup()
      reject(new Error('turnstile_timeout'))
    }, EXECUTE_TIMEOUT_MS)

    try {
      widget_id = turnstile.render(holder, {
        sitekey,
        execution: 'execute',
        // Só renderiza algo se a Cloudflare exigir interação do humano.
        appearance: 'interaction-only',
        callback: (token: string) => {
          cleanup()
          resolve(token)
        },
        'error-callback': () => {
          cleanup()
          reject(new Error('turnstile_error'))
        },
        'timeout-callback': () => {
          cleanup()
          reject(new Error('turnstile_timeout'))
        }
      })
      turnstile.execute(widget_id)
    } catch (err) {
      cleanup()
      reject(err instanceof Error ? err : new Error('turnstile_render_failed'))
    }
  })
}
